import { useState, useEffect } from 'react'
import Link from 'next/link'
import LanguageSwitcher from '../components/LanguageSwitcher'
import SEO from '../components/SEO'
import { LanguageProvider, useLanguage } from '../context/LanguageContext'
import STATUS_COPY from '../locales/status'


export async function getServerSideProps(context) {
  const INTERNAL_API = process.env.INTERNAL_API_URL || 'http://backend:8000'
  let suggestedLanguage = 'en'
  try {
    const cfCountry = context.req.headers['cf-ipcountry'] || ''
    const res = await fetch(`${INTERNAL_API}/v1/geo/`, {
      headers: { 'CF-IPCountry': cfCountry },
    })
    if (res.ok) {
      const data = await res.json()
      if (['en', 'ht', 'fr', 'es', 'pt-BR', 'zh-Hans'].includes(data.suggested_language)) {
        suggestedLanguage = data.suggested_language
      }
    }
  } catch (_) { /* geo hint is best-effort */ }
  return { props: { suggestedLanguage } }
}


export default function SystemStatusPage({ suggestedLanguage }) {
  return (
    <LanguageProvider initialSuggestion={suggestedLanguage}>
      <SystemStatus />
    </LanguageProvider>
  )
}


const LOCALE_DATE_MAP = { en: 'en-US', ht: 'fr-HT', fr: 'fr-FR', es: 'es-ES' }


function SystemStatus() {
  const { language } = useLanguage()
  const t = (STATUS_COPY[language] || STATUS_COPY.en).page
  const dateLocale = LOCALE_DATE_MAP[language] || 'en-US'

  const [statusData, setStatusData] = useState({
    overall: 'operational',
    services: [],
    lastUpdated: null,
    loading: true
  })
  const [incidents, setIncidents] = useState([])
  const [maintenanceData, setMaintenanceData] = useState({ upcoming: [], recent_completed: [] })
  const [subscribeEmail, setSubscribeEmail] = useState('')
  const [subscribeStatus, setSubscribeStatus] = useState({ message: '', type: '' })
  const [isSubscribing, setIsSubscribing] = useState(false)

  // Backend service-code → localized {name, description}
  const serviceMap = {
    'backend': { name: t.service_backend_name, description: t.service_backend_desc },
    'database': { name: t.service_database_name, description: t.service_database_desc },
    'redis': { name: t.service_redis_name, description: t.service_redis_desc },
    'celery': { name: t.service_celery_name, description: t.service_celery_desc },
    'infer_api': { name: t.service_infer_api_name, description: t.service_infer_api_desc },
    'infer_synthesis': { name: t.service_infer_synth_name, description: t.service_infer_synth_desc },
    's3_storage': { name: t.service_s3_name, description: t.service_s3_desc },
    'email': { name: t.service_email_name, description: t.service_email_desc },
  }

  // Backend status/incident/severity string → localized label
  const statusLabel = (s) => ({
    operational: t.status_operational,
    degraded: t.status_degraded,
    'partial-outage': t.status_partial,
    'major-outage': t.status_major,
  }[s] || t.status_unknown)

  const incidentLabel = (s) => ({
    resolved: t.incident_resolved,
    monitoring: t.incident_monitoring,
    identified: t.incident_identified,
    investigating: t.incident_investigating,
  }[s] || s)

  const severityLabel = (s) => ({
    critical: t.severity_critical,
    major: t.severity_major,
  }[s] || t.severity_minor)

  useEffect(() => {
    checkSystemStatus()
    fetchIncidents()
    fetchMaintenance()

    const interval = setInterval(checkSystemStatus, 30000)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchIncidents = async () => {
    try {
      const api = (await import('../services/api')).default
      const response = await api.get('/v1/monitoring/incidents/?days=30')
      setIncidents(response.data.incidents || [])
    } catch (error) {
      console.error('Error fetching incidents:', error)
    }
  }

  const fetchMaintenance = async () => {
    try {
      const api = (await import('../services/api')).default
      const response = await api.get('/v1/monitoring/maintenance/')
      setMaintenanceData(response.data)
    } catch (error) {
      console.error('Error fetching maintenance:', error)
    }
  }

  const handleSubscribe = async () => {
    if (!subscribeEmail || !subscribeEmail.includes('@')) {
      setSubscribeStatus({ message: t.subscribe_err_invalid, type: 'error' })
      return
    }

    setIsSubscribing(true)
    setSubscribeStatus({ message: '', type: '' })

    try {
      const api = (await import('../services/api')).default
      const response = await api.post('/v1/monitoring/subscribe/', { email: subscribeEmail })
      setSubscribeStatus({
        message: response.data.message || t.subscribe_success_default,
        type: 'success'
      })
      setSubscribeEmail('')
    } catch (error) {
      console.error('Error subscribing:', error)
      setSubscribeStatus({
        message: error.response?.data?.error || t.subscribe_err_default,
        type: 'error'
      })
    } finally {
      setIsSubscribing(false)
    }
  }

  const checkSystemStatus = async () => {
    try {
      const api = (await import('../services/api')).default
      const response = await api.get('/v1/monitoring/status/', { timeout: 10000 })

      const data = response.data

      const services = data.services.map(service => ({
        name: serviceMap[service.code]?.name || service.name,
        status: service.status,
        description: serviceMap[service.code]?.description || service.name,
        uptime: service.uptime_24h,
        message: service.message,
        responseTime: service.response_time_ms,
        lastChecked: service.last_checked
      }))

      setStatusData({
        overall: data.overall_status,
        services: services,
        lastUpdated: new Date(data.last_updated),
        loading: false
      })
    } catch (error) {
      console.error('Error checking system status:', error)

      try {
        const api = (await import('../services/api')).default
        const backendResponse = await api.get('/api/health/', { timeout: 5000 })
          .catch(() => ({ data: { status: 'error' } }))

        const services = [{
          name: t.service_backend_name,
          status: backendResponse.data.status === 'healthy' ? 'operational' : 'degraded',
          description: t.service_fallback_desc,
          uptime: 'N/A'
        }]

        setStatusData({
          overall: backendResponse.data.status === 'healthy' ? 'operational' : 'degraded',
          services: services,
          lastUpdated: new Date(),
          loading: false
        })
      } catch (fallbackError) {
        setStatusData(prev => ({
          ...prev,
          overall: 'degraded',
          loading: false
        }))
      }
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'operational':
        return 'text-green-600 dark:text-green-400'
      case 'degraded':
        return 'text-yellow-600 dark:text-yellow-400'
      case 'partial-outage':
        return 'text-orange-600 dark:text-orange-400'
      case 'major-outage':
        return 'text-red-600 dark:text-red-400'
      default:
        return 'text-gray-600 dark:text-gray-400'
    }
  }

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'operational':
        return 'bg-green-100 dark:bg-green-900/20'
      case 'degraded':
        return 'bg-yellow-100 dark:bg-yellow-900/20'
      case 'partial-outage':
        return 'bg-orange-100 dark:bg-orange-900/20'
      case 'major-outage':
        return 'bg-red-100 dark:bg-red-900/20'
      default:
        return 'bg-gray-100 dark:bg-gray-800'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'operational':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        )
      case 'degraded':
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        )
      default:
        return (
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        )
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString(dateLocale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getIncidentColor = (status) => {
    switch (status) {
      case 'resolved':
        return 'border-green-300 dark:border-green-700'
      case 'monitoring':
        return 'border-blue-300 dark:border-blue-700'
      case 'identified':
        return 'border-yellow-300 dark:border-yellow-700'
      case 'investigating':
        return 'border-red-300 dark:border-red-700'
      default:
        return 'border-gray-200 dark:border-gray-700'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 relative">
      <SEO title={t.seo_title} description={t.seo_description} path="/status" lang={language} />
      <LanguageSwitcher />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            {t.h1}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {t.subtitle}
          </p>
        </div>

        {/* Overall Status */}
        <div className={`mb-8 p-6 rounded-lg ${getStatusBgColor(statusData.overall)}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={getStatusColor(statusData.overall)}>
                {getStatusIcon(statusData.overall)}
              </div>
              <div>
                <h2 className={`text-2xl font-semibold ${getStatusColor(statusData.overall)}`}>
                  {statusData.overall === 'operational' ? t.overall_all_ok : t.overall_issues}
                </h2>
                {statusData.lastUpdated && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {t.last_checked} {statusData.lastUpdated.toLocaleTimeString(dateLocale)}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={checkSystemStatus}
              disabled={statusData.loading}
              className="px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              {statusData.loading ? t.btn_checking : t.btn_refresh}
            </button>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          {statusData.services.map((service, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {service.name}
                </h3>
                <div className={`flex items-center space-x-1 ${getStatusColor(service.status)}`}>
                  {getStatusIcon(service.status)}
                  <span className="text-sm">{statusLabel(service.status)}</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {service.description}
              </p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">{t.uptime_label}</span>
                <span className="font-medium text-gray-900 dark:text-white">{service.uptime}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Incidents */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
            {t.incidents_h2}
          </h2>

          {incidents.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400">
                {t.no_incidents}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {incidents.map((incident, index) => (
                <div key={index} className={`border-l-4 ${getIncidentColor(incident.status)} pl-4 py-2`}>
                  <div className="flex items-start justify-between mb-1">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {incident.title}
                        </h3>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          incident.severity === 'critical'
                            ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                            : incident.severity === 'major'
                            ? 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300'
                            : 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                        }`}>
                          {severityLabel(incident.severity)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {incident.description}
                      </p>
                      {incident.affected_services && incident.affected_services.length > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-500 mb-1">
                          {t.affected_prefix} {incident.affected_services.join(', ')}
                        </p>
                      )}
                      {incident.duration_minutes && (
                        <p className="text-xs text-gray-500 dark:text-gray-500">
                          {t.duration_prefix} {Math.floor(incident.duration_minutes / 60)}h {Math.floor(incident.duration_minutes % 60)}m
                        </p>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <span className="text-sm text-gray-500 dark:text-gray-400 block">
                        {formatDate(incident.started_at)}
                      </span>
                      <span className={`inline-block mt-1 text-xs px-2 py-1 rounded ${
                        incident.status === 'resolved'
                          ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                          : incident.status === 'monitoring'
                          ? 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : incident.status === 'identified'
                          ? 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-300'
                          : 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300'
                      }`}>
                        {incidentLabel(incident.status)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Upcoming Maintenance */}
          {maintenanceData.upcoming && maintenanceData.upcoming.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t.maintenance_h3}
              </h3>
              <div className="space-y-3">
                {maintenanceData.upcoming.map((maintenance, index) => (
                  <div key={index} className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-1">
                          {maintenance.title}
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                          {maintenance.description}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">
                          {formatDate(maintenance.scheduled_start)} - {formatDate(maintenance.scheduled_end)}
                        </p>
                      </div>
                      <span className="ml-4 text-xs px-2 py-1 rounded bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300">
                        {maintenance.is_current ? t.maintenance_in_progress : t.maintenance_scheduled}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Subscribe Section */}
        <div className="mt-12 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            {t.subscribe_h3}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {t.subscribe_body}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <input
              type="email"
              placeholder={t.subscribe_placeholder}
              value={subscribeEmail}
              onChange={(e) => setSubscribeEmail(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSubscribe()}
              className="w-full sm:w-auto px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              onClick={handleSubscribe}
              disabled={isSubscribing}
              className={`w-full sm:w-auto px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center ${
                isSubscribing ? 'opacity-75 cursor-not-allowed' : ''
              }`}
            >
              {isSubscribing ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t.subscribe_btn_working}
                </>
              ) : (
                t.subscribe_btn
              )}
            </button>
          </div>
          {subscribeStatus.message && (
            <p className={`mt-3 text-sm ${subscribeStatus.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {subscribeStatus.message}
            </p>
          )}
        </div>

        {/* Footer Links */}
        <div className="mt-12 text-center space-y-2">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t.footer_urgent_before}{' '}
            <Link href="/contact" className="text-blue-600 dark:text-blue-400 hover:underline">
              {t.footer_urgent_link}
            </Link>
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t.footer_api_prefix}{' '}
            <a
              href="https://tts-api.creolecentric.com/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              {t.footer_api_link}
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
